import React from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import { Constants } from "@eagerdog/constants";
import { helperService } from "../../../services/helper.service";
import styles from "./EventDetailsHeader.module.scss";

// Icons - using Unicode symbols for now
const CalendarIcon = () => <span className={styles.icon}>📅</span>;
const LocationIcon = () => <span className={styles.icon}>📍</span>;
const HomeIcon = () => <span className={styles.icon}>🏠</span>;

interface IProps {
  event: any;
}

const EventDetailsHeader: React.FC<IProps> = ({ event }) => {
  const formatDateRange = () => {
    const start = moment(event.start_date);
    const end = moment(event.end_date);
    
    if (start.isSame(end, 'day')) {
      return start.format('MMM D, YYYY');
    }
    
    if (start.isSame(end, 'month')) {
      return `${start.format('MMM D')} - ${end.format('D, YYYY')}`;
    }
    
    return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
  };

  const formatShowTypes = () => {
    if (!event.show_types || event.show_types.length === 0) return '';
    return event.show_types
      .map((st: any) => {
        const showType = typeof st === 'string' ? st : st.show_type;
        return (Constants.show_type as any)[showType] || showType;
      })
      .join(', ');
  };

  const getSanctioningClubBadge = () => {
    const club = event.sanctioning_club;
    if (!club) return null;
    
    return (
      <div className={styles.badge}>
        {(Constants.sanctioning_club as any)[club] || club}
      </div>
    );
  };

  // Extract secretary and chairperson from contacts array
  const secretary = event.contacts?.find((c: any) => c.contact_type === 'secretary');
  const chairperson = event.contacts?.find((c: any) => c.contact_type === 'chairperson');

  return (
    <div className={styles.EventDetailsHeader}>
      <h1 className={styles.title}>{event.title}</h1>
      
      <div className={styles.columns}>
        {/* Column 1: Date & Location */}
        <div className={styles.column}>
          <div className={styles.infoRow}>
            <CalendarIcon />
            <span className={styles.text}>{formatDateRange()}</span>
          </div>
          
          <div className={styles.infoRow}>
            <LocationIcon />
            <div className={styles.textColumn}>
              {event.club_name && (
                <Link to={`/kc/${event.club?.handle}`} className={styles.clubLink}>
                  {event.club_name}
                </Link>
              )}
              {event.address && (
                <span className={styles.address}>
                  {helperService.formatAddress(event.address)}
                </span>
              )}
            </div>
          </div>
          
          {event.conditions && (
            <div className={styles.infoRow}>
              <HomeIcon />
              <span className={styles.text}>{event.conditions}</span>
            </div>
          )}
        </div>

        {/* Column 2: Show Details */}
        <div className={styles.column}>
          <div className={styles.fieldGroup}>
            <div className={styles.label}>Show Types</div>
            <div className={styles.value}>{formatShowTypes()}</div>
          </div>
          
          {event.sanctioning_club && (
            <div className={styles.fieldGroup}>
              <div className={styles.label}>Sanctioned By</div>
              {getSanctioningClubBadge()}
            </div>
          )}
        </div>

        {/* Column 3: Contacts */}
        <div className={styles.column}>
          {secretary && (
            <div className={styles.contact}>
              <div className={styles.label}>Secretary</div>
              <div className={styles.contactName}>
                {secretary.first_name} {secretary.last_name}
              </div>
              {secretary.email && (
                <a href={`mailto:${secretary.email}`} className={styles.contactEmail}>
                  {secretary.email}
                </a>
              )}
              {secretary.phone && (
                <div className={styles.contactPhone}>{secretary.phone}</div>
              )}
            </div>
          )}
          
          {chairperson && (
            <div className={styles.contact}>
              <div className={styles.label}>Chairperson</div>
              <div className={styles.contactName}>
                {chairperson.first_name} {chairperson.last_name}
              </div>
              {chairperson.email && (
                <a href={`mailto:${chairperson.email}`} className={styles.contactEmail}>
                  {chairperson.email}
                </a>
              )}
              {chairperson.phone && (
                <div className={styles.contactPhone}>{chairperson.phone}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailsHeader;

